import React, { useState, useEffect, useRef } from "react";
import ShopifyModal from "./ShopifyModal";
import FacebookModal from "./FacebookModal";
import GoogleAdsModal from "./GoogleAdsModal";
import GoogleAnalyticsModal from "./GoogleAnalyticsModal";
import InstagramModal from "./InstagramModal";
import MetaModal from "./MetaModal";
import { fetchConnectedSources } from "../apiHandlerGlobal"; 
import { useLocation } from "react-router-dom";
import jwtDecode from "jwt-decode";
import { useTranslation } from "react-i18next";
import { useLanguage } from "src/hooks/useLanguage";

interface SourceConnectorProps {
  onConnectionCreated?: (connectorName: string) => void; 
  onNext?: (isValid: boolean) => void;
  onBack?: () => void;
}

interface DecodedToken {
  user_id: string;
  tenant_id: string;
  role_id: string;
  api_key: string;
  company_name: string;
}

const SourceConnector: React.FC<SourceConnectorProps> = ({
  onConnectionCreated,
}) => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const token = params.get("token");
  const hasFetchedRef = useRef(false);
  const { t } = useTranslation();
  useLanguage();

  useEffect(() => {
    if (token) {
      try {
        const decodedToken = jwtDecode<DecodedToken>(token);
        localStorage.setItem("user_id", decodedToken.user_id);
        localStorage.setItem("tenant_id", decodedToken.tenant_id);
        localStorage.setItem("role_id", decodedToken.role_id);
        localStorage.setItem("api_key", decodedToken.api_key);
        localStorage.setItem("company_name", decodedToken.company_name);

        const newUrl = `${window.location.origin}${window.location.pathname}`;
        window.history.replaceState({}, document.title, newUrl);
      } catch (error) {
        console.error("Error decoding token:", token);
      }
    }
  }, [token]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
  }, []);
  const [isShopifyModalOpen, setShopifyModalOpen] = useState<boolean>(false);
  const [isFacebookModalOpen, setFacebookModalOpen] = useState<boolean>(false);
  const [isGoogleAdsModalOpen, setGoogleAdsModalOpen] = useState<boolean>(
    false
  );
  const [isGoogleAnalyticsModalOpen, setGoogleAnalyticsModalOpen] = useState<
    boolean
  >(false);
  const [isInstagramModalOpen, setInstagramModalOpen] = useState<boolean>(
    false
  );
  const [isMetaModalOpen, setMetaModalOpen] = useState<boolean>(false);
  const [connectedConnectors, setConnectedConnectors] = useState<string[]>([]);

  const sources: { [key: number]: string } = {
    1: "Shopify",
    2: "Facebook",
    3: "Instagram",
    4: "GoogleAds",
    5: "GoogleAnalytics",
  };

  const socialMediaImages: Record<string, string> = {
    Shopify: require("./Images/shopify.png"),
    GoogleAds: require("./Images/google.png"),
    GoogleAnalytics: require("./Images/googleanalytics.png"),
    Meta: require("./Images/meta.png"),
    Facebook: require("./Images/facebook.png"),
    Instagram: require("./Images/instagram.png"),
    Snapchat: require("./Images/snapchat.png"),
    Finish: require("./Images/finish.png"),
  };

  const roleID = localStorage.getItem("role_id");

  const socialMediaOrder: string[] = [
    "Shopify",
    "GoogleAds",
    "GoogleAnalytics",
    "Meta",
    "Facebook",
    "Instagram",
    "Snapchat",
    "Finish",
  ];

  const handleConnectorConnected = (connectorName: string) => {
    Object.values(sources).forEach(sourceName => {
      const isConnected = sourceName === connectorName;
      localStorage.setItem(`${sourceName}_connected`, isConnected.toString());
    });
    if (!connectedConnectors.includes(connectorName)) {
      setConnectedConnectors(prev => [...prev, connectorName]);

      if (onConnectionCreated) {
        onConnectionCreated(connectorName);
      }
    }
  };

  useEffect(() => {
    if (hasFetchedRef.current) {return;}
    hasFetchedRef.current = true;
    const fetchSources = async () => {
      const tenantId = localStorage.getItem("tenant_id");
      const apiKey = localStorage.getItem("api_key");
      const shopify_status = localStorage.getItem("Shopify_connected");

      const data = await fetchConnectedSources(tenantId, apiKey);

      if (data && data.status) {
        const connectedNames = data.source_ids
          .map((id: number) => sources[id] ?? null)
          .filter((name): name is string => Boolean(name));

        setConnectedConnectors(connectedNames);
        Object.values(sources).forEach(sourceName => {
          localStorage.setItem(
            `${sourceName}_connected`,
            String(connectedNames.includes(sourceName))
          );
        });
      }
    };

    fetchSources();
  }, []);
  return (
    <>
      <div className="source-connector-header">
        <div>
          <p className="h2design">{t("sourceConnector.title")}</p>
          <p className="finishline">{t("sourceConnector.subtitle")}</p>
        </div>
      </div>
      <div className="icon-grid zigzag-icons-container">
        {socialMediaOrder?.map((socialMedia, index) => (
          <div
            key={index}
            className={`icon frequency-icon ${socialMedia.toLowerCase()}-icon ${
              connectedConnectors.includes(socialMedia) ? "highlight" : ""
            }`}
            style={{
              marginLeft: index % 2 === 0 ? "20px" : "100px",
            }}
          >
            <img
              src={socialMediaImages[socialMedia]}
              alt={socialMedia}
              onClick={() => {
                if (roleID === "1") {
                  if (socialMedia === "Shopify") {
                    setShopifyModalOpen(true);
                  } else if (socialMedia === "Facebook") {
                    setFacebookModalOpen(true);
                  } else if (socialMedia === "GoogleAds") {
                    setGoogleAdsModalOpen(true);
                  } else if (socialMedia === "GoogleAnalytics") {
                    setGoogleAnalyticsModalOpen(true);
                  } else if (socialMedia === "Instagram") {
                    setInstagramModalOpen(true);
                  } else if (socialMedia === "Meta") {
                    setMetaModalOpen(true);
                  }
                }
              }}
              className={`social-media-image ellipse ${socialMedia}`}
            />
          </div>
        ))}
      </div>

      {isShopifyModalOpen && (
        <ShopifyModal
          isOpen={isShopifyModalOpen}
          onClose={() => setShopifyModalOpen(false)}
          onConnected={() => {
            handleConnectorConnected("Shopify");
            setShopifyModalOpen(false);
          }}
        />
      )}

      {isFacebookModalOpen && (
        <FacebookModal
          isOpen={isFacebookModalOpen}
          onClose={() => setFacebookModalOpen(false)}
          onConnected={() => {
            handleConnectorConnected("Facebook");
            setFacebookModalOpen(false);
          }}
        />
      )}

      {isGoogleAdsModalOpen && (
        <GoogleAdsModal
          isOpen={isGoogleAdsModalOpen}
          onClose={() => setGoogleAdsModalOpen(false)}
          onConnected={() => {
            handleConnectorConnected("GoogleAds");
            setGoogleAdsModalOpen(false);
          }}
        />
      )}

      {isGoogleAnalyticsModalOpen && (
        <GoogleAnalyticsModal
          isOpen={isGoogleAnalyticsModalOpen}
          onClose={() => setGoogleAnalyticsModalOpen(false)}
          onConnected={() => {
            handleConnectorConnected("GoogleAnalytics");
            setGoogleAnalyticsModalOpen(false);
          }}
        />
      )}

      {isInstagramModalOpen && (
        <InstagramModal
          isOpen={isInstagramModalOpen}
          onClose={() => setInstagramModalOpen(false)}
          onConnected={() => {
            handleConnectorConnected("Instagram");
            setInstagramModalOpen(false);
          }}
        />
      )}

      {isMetaModalOpen && (
        <MetaModal
          isOpen={isMetaModalOpen}
          onClose={() => setMetaModalOpen(false)}
          onConnected={() => {
            handleConnectorConnected("Meta");
            setMetaModalOpen(false);
          }}
        />
      )}
    </>
  );
};

export default SourceConnector;
